import { el, money, parseMoney } from './ui'
import { currencySymbol } from './round-config'
import { rememberedEach, remember } from './done-extras'
import { lineDrawing } from './picture-tiles'
import type { ExtraOffer } from '../functions/lib/extra-offer-data'
import './offer-extras.css'

export type { ExtraOffer, OfferChoice, OfferRow } from '../functions/lib/extra-offer-data'
const offerPounds = (p: number): string => p === 0 ? 'free' : money(p).replace(/\.00$/, '')
// Plain optional sentences. Never a total or a promise that work is booked.
export function offerSentences(o: ExtraOffer): string[] {
  const itemWords = (e: ExtraOffer['items'][number]): string => e.kind === 'gutters'
    ? `I can also clear your gutters the same day for ${offerPounds(e.count * e.each_pence)}.`
    : e.kind === 'conservatory' ? `I can also clean your conservatory roof the same day for ${offerPounds(e.count * e.each_pence)} (${e.count} panel${e.count === 1 ? '' : 's'} at ${offerPounds(e.each_pence)}).`
    : `I can also do ${e.name} the same day for ${offerPounds(e.count * e.each_pence)}.`
  return [...o.items.filter(e => e.kind === 'gutters').map(itemWords),
    ...o.items.filter(e => e.kind === 'conservatory').map(itemWords),
    ...(o.insides ? [`I can also clean the insides ${o.insides.pence === 0 ? 'for free' : `for an extra ${offerPounds(o.insides.pence)}`}${o.insides.every ? ' each visit' : ' on the first visit'}.`] : []),
    ...o.items.filter(e => e.kind === 'other').map(itemWords)]
}

// Keep ownership of an edited offer across untick/re-tick. Its service prefix is stable
// when its price changes. Each free-form item's full name has its own ownership.
const sentenceKey = (sentence: string): string => sentence.startsWith('I can also do ')
  ? sentence.slice(0, sentence.lastIndexOf(' the same day for '))
  : sentence.split(/ (?:the same day )?for /)[0]!
export function replaceOfferWords(words: string, before: string[], after: string[]): { words: string; changed: boolean; retained: string[] } {
  let next = words
  const retained = before.filter(s => !words.includes(s))
  const blocked = (s: string): boolean => retained.some(old => sentenceKey(old) === sentenceKey(s)) && !words.includes(s)
  const changed = after.some(blocked)
  // Remove just our exact sentence and the separator we inserted, never normalize the owner's words.
  for (const s of before) {
    const at = next.indexOf(s)
    if (at < 0) continue
    if (at > 0 && /[ \n]/.test(next[at - 1]!)) next = next.slice(0, at - 1) + next.slice(at + s.length)
    else if (at === 0 && /[ \n]/.test(next[at + s.length] ?? '')) next = next.slice(s.length + 1)
    else next = next.slice(0, at) + next.slice(at + s.length)
  }
  const additions = after.filter(s => !blocked(s) && !next.includes(s)).join(' ')
  if (additions) {
    const at = next.indexOf('\nKind regards,')
    next = at >= 0 ? `${next.slice(0, at)}\n${additions}${next.slice(at)}` : `${next}${next ? '\n' : ''}${additions}`
  }
  return { words: next, changed, retained }
}
const pence = (t: string): number | null => { if (!t.trim()) return null; try { const n = parseMoney(t); return n !== null && n >= 0 && n <= 1000000 ? n : null } catch { return null } }
const textPrice = (p: number | null): string => p === null ? '' : (p / 100).toFixed(2)

type OfferKind = 'gutters' | 'conservatory' | 'insides' | 'other'
export interface OfferExtrasDraft {
  selected: OfferKind[]; gutter: string; panel: string; panels: number | null; insideText?: string; every: boolean
  others: { name: string; price: string }[]
}

// One model and one set of controls for Write a quote and optional answer offers.
export function offerExtras(opts: { tiles?: boolean; usual?:Partial<Record<'gutters'|'conservatory',number>>; inside: () => number | null; insideAvailable?: boolean; everyDefault?: boolean; initial?: ExtraOffer; draft?: OfferExtrasDraft; onChange: () => void }) {
  type Kind = OfferKind
  const selected = new Set<Kind>()
  let gutter = textPrice(opts.usual?.gutters??rememberedEach('gutters'))
  let panel = textPrice(opts.usual?.conservatory??rememberedEach('conservatory'))
  let panels: number | null = null
  let insideText: string | undefined, every = opts.initial?.insides?.every ?? opts.everyDefault ?? false
  const others: { name: string; price: string }[] = []
  for (const e of opts.initial?.items ?? []) {
    selected.add(e.kind)
    if (e.kind === 'gutters') gutter = textPrice(e.each_pence)
    if (e.kind === 'conservatory') { panel = textPrice(e.each_pence); panels = e.count }
    if (e.kind === 'other') others.push({ name: e.name, price: textPrice(e.each_pence) })
  }
  if (opts.initial?.insides) { selected.add('insides'); insideText = textPrice(opts.initial.insides.pence) }
  if (opts.draft && Array.isArray(opts.draft.selected) && Array.isArray(opts.draft.others)) {
    const d = opts.draft
    selected.clear()
    for (const k of d.selected) if (['gutters', 'conservatory', 'insides', 'other'].includes(k)) selected.add(k)
    if (typeof d.gutter === 'string') gutter = d.gutter
    if (typeof d.panel === 'string') panel = d.panel
    panels = typeof d.panels === 'number' && Number.isInteger(d.panels) ? Math.max(1, Math.min(99, d.panels)) : null
    if (typeof d.insideText === 'string') insideText = d.insideText
    every = !!d.every
    others.splice(0, others.length, ...d.others.filter(o => o && typeof o.name === 'string' && typeof o.price === 'string').slice(0, 8).map(o => ({ ...o })))
  }
  const node = el('div', { class: 'offer-extras' })
  const choices = el('div', { class: 'offer-extras-pills', role: 'group', 'aria-label': 'Offer too?' })
  const fields = el('div', { class: 'offer-extras-fields' })
  node.append(choices, fields)
  const inside = (): number | null => insideText === undefined ? opts.inside() : pence(insideText)
  const value = (): ExtraOffer => {
    const items: ExtraOffer['items'] = []
    if (selected.has('gutters') && pence(gutter) !== null) items.push({ kind: 'gutters', name: 'Gutters', count: 1, each_pence: pence(gutter)! })
    if (selected.has('conservatory') && panels !== null && pence(panel) !== null) items.push({ kind: 'conservatory', name: 'Conservatory roof', count: panels, each_pence: pence(panel)! })
    if (selected.has('other')) for (const o of others) if (o.name.trim() && pence(o.price) !== null) items.push({ kind: 'other', name: o.name.trim(), count: 1, each_pence: pence(o.price)! })
    return { items, insides: selected.has('insides') && inside() !== null ? { pence: inside()!, every } : null }
  }
  const problem = (): string | null => {
    if (selected.has('gutters') && pence(gutter) === null) return 'Put in a price for Gutters, 0 if free.'
    if (selected.has('conservatory') && pence(panel) === null) return 'Put in a price for each panel, 0 if free.'
    if (selected.has('conservatory') && panels === null) return 'Choose how many conservatory roof panels.'
    if (selected.has('insides') && inside() === null) return 'Put in a price for Insides, 0 if free.'
    if (selected.has('other') && others.some(o => !o.name.trim() || o.name.trim().length > 60 || pence(o.price) === null)) return 'Say what the extra is and put in its price, 0 if free.'
    if (value().items.length > 8) return 'At most 8 extras on one visit.'
    return null
  }
  const names: [Kind, string][] = [['gutters','Gutters'], ['conservatory','Conservatory roof'], ['insides','Insides'], ['other','Something else']]
  function paintPills(): void {
    choices.replaceChildren()
    for (const [kind, title] of names) {
      if (kind === 'insides' && opts.insideAvailable === false) continue
      const price = kind === 'gutters' ? pence(gutter) : kind === 'insides' ? inside() : null
      const on = selected.has(kind)
      const caption=`${title}${price === null ? '' : ` ${kind === 'insides' && price > 0 ? '+' : ''}${offerPounds(price)}`}`
      const button = el('button', { type: 'button', class: `link-button offer-extra-pill${on ? ' is-on' : ''}`, 'aria-pressed': String(on), 'data-extra': kind },
        ...(opts.tiles ? [el('span',{class:'offer-extra-tile-top'},lineDrawing(kind === 'gutters' ? 'gutters' : kind === 'conservatory' ? 'roof' : kind === 'insides' ? 'door' : 'dots', 24),el('span',{class:'offer-extra-tile-tick','aria-hidden':'true'},...(on?[lineDrawing('tick',16)]:[]))),el('span',{class:'offer-extra-tile-label'},caption)] : [...(on ? [lineDrawing('tick', 16)] : []),caption]))
      button.addEventListener('click', () => {
        if (on) selected.delete(kind); else { selected.add(kind); if (kind === 'other' && !others.length) others.push({ name: '', price: '' }) }
        draw(); opts.onChange()
      })
      choices.append(button)
    }
  }
  const input = (label: string, text: string, set: (v: string) => void, price = true, shownLabel = label): HTMLElement => {
    const box = el('input', { type: 'text', ...(price ? { inputmode: 'decimal' } : { maxlength: '60' }), 'aria-label': label }) as HTMLInputElement
    box.value = text
    box.addEventListener('input', () => { set(box.value); paintPills(); opts.onChange() })
    return el('label', { class: 'booking-field' }, shownLabel, box)
  }
  function draw(): void {
    paintPills(); fields.replaceChildren()
    if (selected.has('gutters')) fields.append(input('Gutters price', gutter, v => { gutter = v }, true, `Gutters ${currencySymbol()}`))
    if (selected.has('conservatory')) {
      const count = el('span', { class: 'offer-panel-count', 'aria-live': 'polite' }, panels === null ? '' : String(panels))
      const minus = el('button', { type: 'button', class: 'link-button', 'aria-label': 'One panel fewer' }, '−')
      const plus = el('button', { type: 'button', class: 'link-button', 'aria-label': 'One panel more' }, '+')
      const paintCount = (): void => { count.textContent = panels === null ? '' : String(panels); minus.disabled = panels === null || panels <= 1; plus.disabled = panels === 99 }
      minus.addEventListener('click', () => { if (panels !== null && panels > 1) panels--; paintCount(); opts.onChange() })
      plus.addEventListener('click', () => { panels = Math.min(99, (panels ?? 0) + 1); paintCount(); opts.onChange() })
      paintCount()
      fields.append(el('fieldset', { class: 'offer-conservatory-price' }, el('legend', {}, 'Conservatory roof'),
        el('div', { class: 'offer-conservatory-fields' },
          el('div', { class: 'offer-panel-field' }, el('span', {}, 'How many panels?'),
            el('div', { class: 'offer-panel-counter', role: 'group', 'aria-label': 'How many panels?' }, minus, count, plus)),
          input('A panel', panel, v => { panel = v }, true, `A panel ${currencySymbol()}`))))
    }
    if (selected.has('insides')) {
      fields.append(input('Insides price', insideText ?? textPrice(opts.inside()), v => { insideText = v }))
      const check = el('input', { type: 'checkbox' }) as HTMLInputElement
      check.checked = every
      check.addEventListener('change', () => { every = check.checked; opts.onChange() })
      if (opts.everyDefault !== undefined) fields.append(el('label', { class: 'check-label' }, check, 'Insides every visit'))
    }
    if (selected.has('other')) {
      for (const o of others) fields.append(el('div', { class: 'offer-other-row' }, input('What it is', o.name, v => { o.name = v }, false), input('Its price', o.price, v => { o.price = v })))
      const more = el('button', { type: 'button', class: 'link-button' }, '+ Another')
      more.addEventListener('click', () => { if (others.length < 8) others.push({ name: '', price: '' }); draw(); opts.onChange() })
      fields.append(more)
    }
  }
  draw()
  return { node, fields, value, problem, draft: (): OfferExtrasDraft => ({ selected: [...selected], gutter, panel, panels, insideText, every, others: others.map(o => ({ ...o })) }), has: (k: Kind) => selected.has(k), refresh: draw, remember: () => { for (const e of value().items) if (e.kind !== 'other') remember(e.kind, e.each_pence) } }
}
